import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'
import { useFormikContext } from 'formik'
import isEqual from 'lodash-es/isEqual'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
  NOTIFICATION_TYPE,
} from '../../../../constants/common'
import { FILE_SCOPE_EMAIL } from '../../../../constants/file'
import { NOTIFICATION_FILE_UPLOAD_LIMIT } from '../../../../constants/notification'
import { SIZE } from '../../../../constants/size'
import { FORMIK_EMPTY_VALUE } from '../../../../constants/validation'
import {
  useDragOverZone,
  useInitData,
  useInlineNotifications,
  useScreen,
} from '../../../../hooks'
import {
  isInfoEmailAccount,
  isServiceEmailAccount,
  isSystemEmailAccount,
  removeReplyBlock,
} from '../../../../utils/email'
import { preventDefault } from '../../../../utils/events'
import { getBytesFromMegabytes } from '../../../../utils/file'
import { hasTinyMceHtmlVisibleText } from '../../../../utils/html'
import { isFormMobileActions } from '../../../../utils/screen'
import { removeSignature } from '../../../../utils/signature'
import { AITextGenerationType } from '../../../AITextGeneration'
import { AITextGenerationField } from '../../../AITextGenerationField'
import { AttachActionSelect } from '../../../AttachActionSelect'
import {
  FileAttachTag,
  FileList,
  FileUploadConstantInputWrap,
  useAttachStorageFiles,
  useFileDropzone,
  useFileUpload,
} from '../../../File'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../../FilesPreviewGallery'
import {
  FormActionsAttachContainer,
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
} from '../../../Form'
import { useFormikFieldValue } from '../../../Formik'
import { Content } from '../../../Modal'
import { TAG_HEIGHT } from '../../../Tag'
import { useWysiwygEditorFullScreenHeight } from '../../../WysiwygEditor'
import { EMAIL_NEED_RECONNECT } from '../../Email.constants'
import { useEmailDraft } from '../../Email.hooks'
import {
  EmailCreationFormActions,
  EmailCreationFormHelpersActions,
} from '../../EmailCreationFormActions'
import { EmailCreationFormMassMailingCheckbox } from '../../EmailCreationFormMassMailingCheckbox'
import { EmailFormContext } from '../../EmailForm.context'
import {
  EmailReplyFormMobileMoreActions,
  isEmailReplyFormMobileActions,
} from '../../EmailReplyFormActions'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES,
  EMAIL_CREATION_FORM_FIELD_NAME_FILES,
  EMAIL_CREATION_FORM_FIELD_NAME_FILES_AS_LINKS,
  EMAIL_CREATION_FORM_FIELD_NAME_FILES_FROM_STORAGE,
  EMAIL_CREATION_FORM_FIELD_NAME_FROM,
  EMAIL_CREATION_FORM_FIELD_NAME_MASS_MAILING,
  EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE,
  EMAIL_CREATION_FORM_FIELD_NAME_REPLY_SUBJECT,
  EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TO,
  EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE,
  EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE_FILES,
  EMAIL_CREATION_FORM_FIELD_NAME_SIGNATURE,
  EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  EMAIL_CREATION_FORM_NOTIFICATION_AI_DRAFT_NOT_APPLIED,
  EMAIL_CREATION_FORM_NOTIFICATION_NO_ACTIVE_ACCOUNTS,
  EMAIL_CREATION_FORM_TEMPLATE_FOLDER_PRIVATE,
  PRIVATE_ACC_FILE_MAX_SIZE,
  SYSTEM_ACC_FILE_MAX_SIZE,
} from '../EmailCreationForm.constants'
import {
  useAccountsBlackList,
  useAttach,
  useCollapsedHeader,
  useDuplicateRecipientsValidation,
  useEmailPreview,
  useEmailRecipientsAutoAdd,
  useGoogleTemplate,
  useMassMailingCcBccNotification,
  useMoreActionsAvailability,
  useNeedReconnectNotification,
  usePrinterForNoEmailAccounts,
  useTemplate,
  useTestVariables,
} from '../EmailCreationForm.hooks'
import {
  getEmailFormTemplateApiParams,
  getEmailFormTemplateFile,
  isEmailCreationFormNotificationAiDraftNotApplied,
  isEmailCreationFormNotificationNoActiveAccounts,
} from '../EmailCreationForm.utils'
import { EmailMessageDragOverlay } from '../EmailMessageDragOverlay'
import { FromField } from '../FromField'
import { MessageField } from '../MessageField'
import { ReplyToField } from '../ReplyToField'
import { SubjectField } from '../SubjectField'
import { TemplateField } from '../TemplateField'
import { ToField } from '../ToField'

import { useEmailFormFieldsValidation } from './FormFields.hooks'
import {
  canSubmit,
  getAIApplyNextValue,
  getFilesAsLinks,
  getTemplateFolder,
  hasEmailFormUserWrittenSubject,
  splitFilesToLinksAndAttachments,
} from './FormFields.utils'

import * as styles from './FormFields.module.css'

function FormFields({
  Actions = EmailCreationFormActions,
  ContentComponent = Content,
  FromFieldComponent = FromField,
  // Temporarily hidden in every email form (ID-135); restore `PriorityField`
  // as the default to bring the button back. The table flag stays visible.
  PriorityFieldComponent = null,
  aiLocalContext,
  attachProps = EMPTY_OBJECT,
  beforeContent = null,
  blackList,
  contentComponentProps = {
    size: SIZE.X_LARGE,
  },
  disableDuplicateRecipientsValidation = false,
  excludeAutoAddAdditionalContacts = false,
  fieldsProps,
  fileUploadLimitProps,
  getPreviewDependencies,
  hideCcBcc,
  hideMassMailing = hideCcBcc,
  initialNotifications,
  isAvailablePreview,
  isAvailableTestVariables,
  isEmailReplyForm,
  isFilesAsLinksEnabled = true,
  setBlackList,
  singleTemplateFolder = EMAIL_CREATION_FORM_TEMPLATE_FOLDER_PRIVATE,
  templateConfig,
  templateEntityApiParams,
  templateListApiParams,
  onAIModeChange,
  onCancel,
  onClose,
}) {
  const { ai_features: aiFeatures } = useInitData()

  const {
    /* blocked: behaves like disabled (no clicks, no value changes),
      but keeps the normal visual style (no opacity change) and
      shows a "not-allowed" cursor. Unlike "readonly", it should not
      handle clicks or focus changes at all. */
    blocked,
    formBodyActionsProps = EMPTY_OBJECT,
    getIsDirty,
    isDragging,
    isFullScreen,
    setModal,
    onCloseModal,
    onDragOver,
    onDrop,
  } = useContext(EmailFormContext)

  const { contentRef, contentWrapperRef, editorRef, handleEditorInit } =
    useWysiwygEditorFullScreenHeight({ isFullScreen })

  const {
    dirty,
    errors,
    initialValues,
    isSubmitting,
    setFieldError,
    setFieldValue,
    setSubmitting = EMPTY_OBJECT,
    validateForm,
    values,
  } = useFormikContext()

  const { t } = useTranslation()

  const { width: screenWidth } = useScreen()

  const { [EMAIL_CREATION_FORM_FIELD_NAME_FROM]: initialAccount } =
    initialValues

  const noActiveAccounts =
    initialAccount !== undefined && !Number.isInteger(initialAccount?.value)

  const [files, setFiles] = useFormikFieldValue(
    EMAIL_CREATION_FORM_FIELD_NAME_FILES
  )

  const [storageFiles, setStorageFiles] = useFormikFieldValue(
    EMAIL_CREATION_FORM_FIELD_NAME_FILES_FROM_STORAGE
  )

  const [filesAsLinks, setFilesAsLinks] = useFormikFieldValue(
    EMAIL_CREATION_FORM_FIELD_NAME_FILES_AS_LINKS
  )

  const prevPreparedFilesRef = useRef(EMPTY_LIST)

  const [mass, setMass] = useFormikFieldValue(
    EMAIL_CREATION_FORM_FIELD_NAME_MASS_MAILING
  )

  const [docTemplates, setDocTemplates] = useFormikFieldValue(
    EMAIL_CREATION_FORM_FIELD_NAME_DOCUMENTS_TEMPLATES
  )

  const [from, setFrom] = useFormikFieldValue(
    EMAIL_CREATION_FORM_FIELD_NAME_FROM
  )

  const [signature] = useFormikFieldValue(
    EMAIL_CREATION_FORM_FIELD_NAME_SIGNATURE
  )

  const [messageValue] = useFormikFieldValue(
    EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE
  )

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications({ initialNotifications })

  const isAIDraftActiveRef = useRef(false)

  const handleAIModeChange = useCallback(
    isDraftMode => {
      isAIDraftActiveRef.current = isDraftMode

      onAIModeChange?.(isDraftMode)

      if (!isDraftMode) {
        handleRemoveNotification(
          EMAIL_CREATION_FORM_NOTIFICATION_AI_DRAFT_NOT_APPLIED
        )
      }
    },
    [handleRemoveNotification, onAIModeChange]
  )

  const isAIError = Boolean(
    notifications.find(n =>
      isEmailCreationFormNotificationAiDraftNotApplied(n.id)
    )
  )

  useEffect(() => {
    if (noActiveAccounts) {
      handleAddNotification({
        id: EMAIL_CREATION_FORM_NOTIFICATION_NO_ACTIVE_ACCOUNTS,
        linkProps: {
          reloadDocument: true,
          title: t('Notification.ConnectEmailLinkKey'),
          to: `/settings/email-integration`,
        },
        text: t('Error.NoActiveAccountErrorKey'),
        type: NOTIFICATION_TYPE.error,
      })
    }
  }, [noActiveAccounts])

  const { draft: emailDraft, setDraft: setEmailDraft } = useEmailDraft()

  useEffect(() => {
    const isDirty = getIsDirty({
      dirty,
      files: EMPTY_LIST,
      initialValues,
      values,
    })

    if (isDirty) {
      setEmailDraft(values)
    }
  }, [dirty, initialValues, values])

  const isSystemAccount = isSystemEmailAccount(from)

  const maxSize = isSystemAccount
    ? SYSTEM_ACC_FILE_MAX_SIZE
    : PRIVATE_ACC_FILE_MAX_SIZE

  const maxSizeBytes = getBytesFromMegabytes(maxSize)

  const preparedFiles = useMemo(() => {
    return [
      ...storageFiles,
      ...files,
      ...docTemplates.map(getEmailFormTemplateFile),
    ]
  }, [docTemplates, files, storageFiles])

  useEffect(() => {
    if (fileUploadLimitProps) {
      const { isFileLimit, notification } = fileUploadLimitProps

      const filesSize = preparedFiles.reduce(
        (totalSize, file) => totalSize + (Number(file?.size) || 0),
        0
      )

      const hasFileLimit = isFileLimit({
        filesSize,
        maxSize: maxSizeBytes,
      })

      if (hasFileLimit) {
        handleAddNotification(
          notification || {
            id: NOTIFICATION_FILE_UPLOAD_LIMIT,
            text: t('Form.UploadFilesLimitErrorKey', { count: maxSize }),
            type: NOTIFICATION_TYPE.error,
          }
        )
      } else {
        handleRemoveNotification(
          notification?.id || NOTIFICATION_FILE_UPLOAD_LIMIT
        )
      }
    }
  }, [
    fileUploadLimitProps,
    isFilesAsLinksEnabled,
    maxSize,
    maxSizeBytes,
    preparedFiles,
  ])

  useEffect(() => {
    if (!isFilesAsLinksEnabled) {
      if (filesAsLinks.length > 0) {
        setFilesAsLinks(EMPTY_LIST)
      }
    } else {
      const nextFilesAsLinks = getFilesAsLinks({
        files: preparedFiles,
        filesAsLinks,
        maxSizeBytes,
        prevFiles: prevPreparedFilesRef.current,
      })

      if (!isEqual(nextFilesAsLinks, filesAsLinks)) {
        setFilesAsLinks(nextFilesAsLinks)
      }
    }

    prevPreparedFilesRef.current = preparedFiles
  }, [
    filesAsLinks,
    isFilesAsLinksEnabled,
    maxSizeBytes,
    preparedFiles,
    setFilesAsLinks,
  ])

  useEffect(() => {
    if (
      (isInfoEmailAccount(from) && !mass) ||
      (isServiceEmailAccount(from) && mass)
    ) {
      setFrom(FORMIK_EMPTY_VALUE)
    }
  }, [mass])

  useEffect(() => {
    if (
      notifications.find(i =>
        isEmailCreationFormNotificationNoActiveAccounts(i.id)
      )
    ) {
      setFieldError(EMAIL_CREATION_FORM_FIELD_NAME_FROM, true)
      setFieldError(EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TO, true)
    }
  }, [notifications])

  useEffect(() => {
    setFieldValue(EMAIL_CREATION_FORM_FIELD_NAME_REPLY_TO, FORMIK_EMPTY_VALUE)
  }, [values[EMAIL_CREATION_FORM_FIELD_NAME_FROM]])

  const { accountsNoEmails, printer } = usePrinterForNoEmailAccounts({
    removeNotification: handleRemoveNotification,
    setNotification: handleAddNotification,
  })

  const {
    enabled: autoAddEnabled,
    expandSelectedRecipientEmails,
    setEnabled: setAutoAddEnabled,
  } = useEmailRecipientsAutoAdd({
    disabled: excludeAutoAddAdditionalContacts,
  })

  const { duplicateOptionKeys, validateNoDuplicates } =
    useDuplicateRecipientsValidation({
      printer,
      removeNotification: handleRemoveNotification,
      setNotification: handleAddNotification,
    })

  const {
    filesError,
    filesUploading,
    handleExcludeFile,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onSetFiles: setFiles,
  })

  const { handleAddStorageFile, handleRemoveStorageFile } =
    useAttachStorageFiles({
      onSetFiles: setStorageFiles,
    })

  const { attachmentFiles, linkedFiles } = useMemo(() => {
    return splitFilesToLinksAndAttachments({
      files: preparedFiles,
      filesAsLinks: isFilesAsLinksEnabled ? filesAsLinks : EMPTY_LIST,
    })
  }, [filesAsLinks, isFilesAsLinksEnabled, preparedFiles])

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({
      files: preparedFiles,
    })

  useMassMailingCcBccNotification({
    mass,
    removeNotification: handleRemoveNotification,
    setNotification: handleAddNotification,
  })

  const { handleCheckBlackList } = useAccountsBlackList({
    accountId: values[EMAIL_CREATION_FORM_FIELD_NAME_FROM]?.value,
    blackList,
    removeNotification: handleRemoveNotification,
    setBlackList,
    setNotification: handleAddNotification,
  })

  useNeedReconnectNotification({
    accountId: values[EMAIL_CREATION_FORM_FIELD_NAME_FROM]?.value,
    errors,
    handleCloseModal: onClose,
    needReconnect:
      values[EMAIL_CREATION_FORM_FIELD_NAME_FROM]?.[EMAIL_NEED_RECONNECT],
    removeNotification: handleRemoveNotification,
    setError: setFieldError,
    setNotification: handleAddNotification,
  })

  const { collapsedHeader, handleCollapseHeader, setCollapsedHeader } =
    useCollapsedHeader({ blackList, isFullScreen, notifications })

  const handleDeleteFile = useCallback(
    id => {
      if (
        values[
          EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE_FILES
        ]?.includes(id)
      ) {
        handleExcludeFile(id)
      } else {
        handleRemoveFile(id)
      }
    },
    [files, values[EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE_FILES]]
  )

  function handleDeleteAttachment(file) {
    if (file.template) {
      handleDeleteGoogleTemplate(file.template)
    } else if (storageFiles.find(i => i.id === file.id)) {
      handleRemoveStorageFile(file.id)
    } else if (file.forwarded) {
      // Owned by the forwarded message: detach from this email, keep the file.
      handleExcludeFile(file.id)
    } else {
      handleDeleteFile(file.id)
    }
  }

  function handleRemoveFileLinksBlock() {
    const idsSet = new Set(filesAsLinks.map(id => Number(id)))

    const nextFiles = files.filter(file => !idsSet.has(Number(file.id)))
    const nextStorageFiles = storageFiles.filter(
      file => !idsSet.has(Number(file.id))
    )

    setFiles(nextFiles)
    setStorageFiles(nextStorageFiles)
    setFilesAsLinks(EMPTY_LIST)
  }

  function handleClickFileTag(file) {
    handleClickFile(file.id)
  }

  function renderFile(file, key) {
    return (
      <FileAttachTag
        file={file}
        height={TAG_HEIGHT.height40}
        isDisabled={isSubmitting}
        key={key}
        onClick={file.template ? undefined : handleClickFileTag}
        onRemove={blocked ? undefined : handleDeleteAttachment}
      />
    )
  }

  useEmailFormFieldsValidation({
    filesError,
    filesUploading,
    onRemoveNotification: handleRemoveNotification,
    onSetNotification: handleAddNotification,
  })

  const handleCheckOnSubmit = useCallback(
    e => {
      preventDefault(e)

      if (isAIDraftActiveRef.current) {
        handleAddNotification({
          id: EMAIL_CREATION_FORM_NOTIFICATION_AI_DRAFT_NOT_APPLIED,
          text: t('AITextGeneration.DraftNotAppliedErrorKey'),
          type: NOTIFICATION_TYPE.error,
        })

        return
      }

      if (!disableDuplicateRecipientsValidation && !validateNoDuplicates()) {
        return
      }

      if (canSubmit({ filesError, filesUploading, notifications })) {
        setSubmitting(true)

        handleCheckBlackList()
      } else {
        validateForm()
      }
    },
    [
      disableDuplicateRecipientsValidation,
      filesError,
      filesUploading,
      handleAddNotification,
      handleCheckBlackList,
      notifications,
      setSubmitting,
      t,
      validateNoDuplicates,
    ]
  )

  const handleCancel = useCallback(() => {
    onCancel?.({ dirty, files, values })
  }, [dirty, files, onCancel, values])

  // Strip signature only — reply block is kept so the AI can use it as context
  // and so hasFieldContent (which drives the regenerate button) stays true.
  const messageWithoutSignature = useMemo(
    () => removeSignature(messageValue || EMPTY_STRING),
    [messageValue]
  )

  // Strip reply block on top of signature so hasExistingContent only reflects
  // text the user actually wrote (drives Replace vs Insert Below in draft panel).
  const userWrittenContent = useMemo(
    () =>
      isEmailReplyForm
        ? removeReplyBlock(messageWithoutSignature)
        : messageWithoutSignature,
    [isEmailReplyForm, messageWithoutSignature]
  )

  const {
    [EMAIL_CREATION_FORM_FIELD_NAME_REPLY_SUBJECT]: replySubject,
    [EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT]: subject,
  } = values

  const hasExistingContent =
    hasTinyMceHtmlVisibleText(userWrittenContent) ||
    hasEmailFormUserWrittenSubject({ replySubject, subject })

  const handleApplyAIText = useCallback(
    (generatedText, currentValue, action) => {
      const nextValue = getAIApplyNextValue({
        action,
        currentValue,
        generatedText,
        ignoreReplyBlock: isEmailReplyForm,
        insertSignatureBeforeQuotedText:
          from?.insert_signature_before_quoted_text,
        signatureHtml: signature,
      })

      editorRef.current?.setContent(nextValue)

      return nextValue
    },
    [from?.insert_signature_before_quoted_text, isEmailReplyForm, signature]
  )

  const handleSetMassMailing = useCallback(() => {
    setMass(true)
  }, [])

  const templateParams = useMemo(
    () =>
      getEmailFormTemplateApiParams({
        mass,
        recipients: values[EMAIL_CREATION_FORM_FIELD_NAME_TO],
        templateEntityApiParams,
      }),
    [mass, templateEntityApiParams, values[EMAIL_CREATION_FORM_FIELD_NAME_TO]]
  )

  useTemplate({
    addFiles: handleUploadFile,
    config: templateConfig,
    deleteFile: handleExcludeFile,
    editorRef,
    params: templateParams,
    templateEntityApiParams,
  })

  const templateFolder = getTemplateFolder({ mass, singleTemplateFolder })

  const docTemplateCheckRef = useRef(null)

  const { handleDeleteGoogleTemplate, handleOpenGoogleTemplate } =
    useGoogleTemplate({
      docTemplateCheckRef,
      setDocTemplates,
      templateEntityApiParams,
      templateFolder,
    })

  const { handlePreview } = useEmailPreview({
    files: attachmentFiles,
    getPreviewDependencies,
    linkedFiles,
    templateEntityApiParams,
    withFromField: !!FromFieldComponent,
    onDeleteAttachment: handleDeleteAttachment,
    onSend: handleCheckOnSubmit,
  })

  const { handleTestVariables, isTesting } = useTestVariables({
    docTemplateCheckRef,
    docTemplates,
    mass,
    textTemplate: values[EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE],
    onDeleteTemplate: handleDeleteGoogleTemplate,
    onEditTemplate: handleOpenGoogleTemplate,
  })

  const showFiles =
    attachmentFiles?.length > 0 ||
    filesUploading?.length > 0 ||
    filesError?.length > 0

  function handleInitEditor(editor) {
    handleEditorInit(editor)

    fieldsProps?.[EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]?.onInit?.(editor)
  }

  useEffect(() => {
    editorRef.current?.selection.select(editorRef.current.getBody(), true)
    editorRef.current?.selection.collapse(false)
    editorRef.current?.focus()
  }, [values[EMAIL_CREATION_FORM_FIELD_NAME_SELECTED_TEMPLATE]])

  const {
    availableMore,
    availablePreview,
    availableTestVariables,
    emptyAccountField,
    emptyRecipients,
    recipientsCount,
  } = useMoreActionsAvailability({
    blackList,
    blocked,
    disableDuplicateRecipientsValidation,
    docTemplates,
    isAvailablePreview,
    isAvailableTestVariables,
    mass,
    noActiveAccounts,
    printer,
  })

  const { attachActionOptions, handleAttachAction } = useAttach({
    ...attachProps,
    selectedStorageFiles: storageFiles,
    onAttachFilesFromStorage: handleAddStorageFile,
    onCloseModal,
    onOpenGoogleTemplate: handleOpenGoogleTemplate,
    onSetModal: setModal,
  })

  const {
    getInputProps: getMobileFileInputProps,
    open: handleMobileOpenFileAttach,
  } = useFileDropzone({
    onCloseModal,
    onFinishUpload: handleUploadFile,
    onSetModal: setModal,
    onStartUpload: handleStartUploading,
    onUploadErrors: handleUploadErrors,
  })

  const massMailingActionsProps = {
    availableMore,
    availablePreview,
    availableTestVariables,
    emptyAccountField,
    emptyRecipients,
    onPreview: handlePreview,
    onTestVariables: handleTestVariables,
  }

  const {
    attachOver,
    handleDragAttachLeave,
    handleDragAttachOver,
    handleDragInlineLeave,
    handleDragInlineOver,
    inlineOver,
  } = useDragOverZone({ onDragOver })

  const handleChangeMass = useCallback(() => {
    setMass(!mass)
  }, [mass, setMass])

  const fieldsContainerClassName = classnames(styles.fieldsContainer, {
    [styles.fieldsContainerDragging]: isDragging && !isSubmitting,
  })

  const uploadProps = {
    disabled: blocked,
    options: attachActionOptions,
    placeholder: 'Email.AttachActionKey',
    onClick: handleAttachAction,
    onDrop,
    onFinishUpload: handleUploadFile,
    onStartUpload: handleStartUploading,
    onUploadErrors: handleUploadErrors,
  }

  const { onMakeFormFullScreen, onRemoveDraft } = formBodyActionsProps

  const isMobileActions = isEmailReplyForm
    ? isEmailReplyFormMobileActions({ screenWidth })
    : isFormMobileActions({
        screenWidth,
      })

  function handleMakeFormFullScreen() {
    onMakeFormFullScreen(values)
  }

  const messageDragOverlay = (
    <EmailMessageDragOverlay
      {...(!isSubmitting
        ? {
            isDragging,
            isOver: inlineOver,
            onDragLeave: handleDragInlineLeave,
          }
        : null)}
      className={
        aiFeatures?.text_generation ? undefined : styles.emailMessageDragOverlay
      }
    >
      <MessageField
        {...(!isSubmitting
          ? { onDragOver: handleDragInlineOver, onDrop }
          : null)}
        fieldsProps={fieldsProps?.[EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]}
        linkedFiles={linkedFiles}
        onClickFile={handleClickFile}
        onFocusEditor={handleCollapseHeader}
        onInitEditor={handleInitEditor}
        onRemoveFileLinksBlock={handleRemoveFileLinksBlock}
      />
    </EmailMessageDragOverlay>
  )

  return (
    <FileUploadConstantInputWrap
      getFileAttachInputProps={getMobileFileInputProps}
    >
      <FormBody
        Actions={Actions}
        actionsProps={{
          ...massMailingActionsProps,
          ...formBodyActionsProps,
          ButtonAdd: false,
          ButtonClear: false,
          Container: FormActionsAttachContainer,
          More:
            isMobileActions && isEmailReplyForm
              ? EmailReplyFormMobileMoreActions
              : null,
          UploadComponent:
            isMobileActions && isEmailReplyForm ? null : AttachActionSelect,
          cancelProps: {
            onClick: handleCancel,
          },
          className: styles.actions,
          containerProps: {
            ...(!isSubmitting
              ? {
                  isDragging,
                  isOver: attachOver,
                  onDragLeave: handleDragAttachLeave,
                  onDragOver: handleDragAttachOver,
                  onDrop,
                }
              : null),
            uploadProps,
          },
          hideMassMailing,
          isMobileActions,
          isSubmitting,
          isTesting,
          mass,
          moreProps:
            isMobileActions && isEmailReplyForm
              ? {
                  attachActionOptions,
                  disabled: blocked,
                  onAttachAction: handleAttachAction,
                  onMakeFormFullScreen: handleMakeFormFullScreen,
                  onMobileOpenFileAttach: handleMobileOpenFileAttach,
                  onRemoveDraft: emailDraft ? onRemoveDraft : null,
                }
              : null,
          submitProps: {
            isLoading: isSubmitting,
            title: mass ? 'Email.MassMailingSendBtnKey' : 'Email.SendBtnKey',
            translationParams: mass ? { count: recipientsCount } : null,
            onClick: handleCheckOnSubmit,
          },
          uploadProps,
          onChangeMass: handleChangeMass,
        }}
      >
        <ContentComponent
          {...contentComponentProps}
          contentRef={contentRef}
          contentWrapperRef={contentWrapperRef}
          isFullScreen={isFullScreen}
          shadow={
            !isDragging
              ? (contentComponentProps.shadow ?? collapsedHeader)
              : false
          }
        >
          <FormInlineNotifications
            initialNotifications={initialNotifications}
            notifications={notifications}
          />

          <FormFieldsContainer className={fieldsContainerClassName}>
            {beforeContent}
            {FromFieldComponent ? (
              <div className={styles.fromRow}>
                <FromFieldComponent
                  fieldProps={
                    fieldsProps?.[EMAIL_CREATION_FORM_FIELD_NAME_FROM]
                  }
                  mass={mass}
                />
                {PriorityFieldComponent ? (
                  <PriorityFieldComponent className={styles.priorityField} />
                ) : null}
              </div>
            ) : null}
            {FromFieldComponent &&
            isInfoEmailAccount(values[EMAIL_CREATION_FORM_FIELD_NAME_FROM]) ? (
              <ReplyToField />
            ) : null}
            <ToField
              accountsNoEmails={accountsNoEmails}
              autoAddEnabled={autoAddEnabled}
              blackList={blackList}
              collapsedHeader={collapsedHeader}
              duplicateOptionKeys={duplicateOptionKeys}
              fieldsProps={fieldsProps}
              hideCcBcc={hideCcBcc}
              mass={mass}
              printer={printer}
              removeNotification={handleRemoveNotification}
              setCollapsedHeader={setCollapsedHeader}
              setMassMailing={handleSetMassMailing}
              setNotification={handleAddNotification}
              onChangeAutoAddEnabled={
                excludeAutoAddAdditionalContacts ? null : setAutoAddEnabled
              }
              onExpandSelectedRecipientEmails={expandSelectedRecipientEmails}
            />
            <SubjectField />
            <TemplateField apiParams={templateListApiParams} mass={mass} />
          </FormFieldsContainer>
          {aiFeatures?.text_generation ? (
            <AITextGenerationField
              className={styles.aiGenerationField}
              fieldContent={messageWithoutSignature}
              fieldName={EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE}
              hasExistingContent={hasExistingContent}
              isError={isAIError}
              localContext={aiLocalContext}
              subjectFieldName={EMAIL_CREATION_FORM_FIELD_NAME_SUBJECT}
              type={AITextGenerationType.Email}
              onApplyText={handleApplyAIText}
              onModeChange={handleAIModeChange}
            >
              {messageDragOverlay}
            </AITextGenerationField>
          ) : (
            messageDragOverlay
          )}
          {showFiles && (!isDragging || isSubmitting) ? (
            <FileList
              className={classnames(styles.fileList, {
                [styles.replyFormFileList]: isEmailReplyForm,
              })}
              errorItems={filesError}
              files={attachmentFiles}
              itemClassName={styles.fileListItem}
              renderItem={renderFile}
              uploadingItems={filesUploading}
              onRemoveFile={handleRemoveFile}
            />
          ) : null}
          {isMobileActions && !hideMassMailing ? (
            <div className={styles.mobileMassActions}>
              <EmailCreationFormMassMailingCheckbox
                checked={mass}
                onChange={handleChangeMass}
              />
              {availableMore ? (
                <EmailCreationFormHelpersActions
                  {...massMailingActionsProps}
                  disabled={isSubmitting}
                  disablePreview={!availablePreview}
                  disableVariables={!availableTestVariables}
                  isTesting={isTesting}
                  mass={mass}
                />
              ) : null}
            </div>
          ) : null}
        </ContentComponent>
        <FilesPreviewGallery
          files={[...storageFiles, ...files]}
          initActive={activeFileIndex}
          scope={FILE_SCOPE_EMAIL}
          onClose={handleCloseFilePreview}
        />
      </FormBody>
    </FileUploadConstantInputWrap>
  )
}

export default FormFields
